import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { billingRateAPI } from '../services/api';
import type { BillingRatePublic } from '../types';
import UnbilledSummary from './UnbilledSummary';

const Home: React.FC = () => {
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const [myRates, setMyRates] = useState<BillingRatePublic[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchMyRates = async () => {
      // Fetch rates for all users (admins and regular users)
      try {
        setLoading(true);
        const response = await billingRateAPI.getMyRates();
        setMyRates(response.data);
      } catch (error) {
        console.error('Failed to fetch billing rates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMyRates();
  }, []);

  return (
    <div className="container">
      <div className="card">
        <h2>Welcome to the Invoicing App</h2>
        <p style={{ marginBottom: '2rem', color: '#7f8c8d' }}>
          {isAdmin
            ? 'Manage companies, purchase orders, and create professional invoices with ease.'
            : 'Welcome! Your role provides view-only access. Time tracking features will be available soon.'}
        </p>

        {isAdmin && (
          <>
            {/* Unbilled Time Summary Widget */}
            <UnbilledSummary maxItems={5} showViewAllLink={true} />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
              <div style={{ padding: '2rem', backgroundColor: '#ecf0f1', borderRadius: '8px' }}>
                <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Companies</h3>
                <p style={{ marginBottom: '1.5rem', color: '#7f8c8d' }}>
                  Manage your company database and keep track of their information.
                </p>
                <Link to="/customers" className="btn btn-primary">
                  View Companies
                </Link>
              </div>

              <div style={{ padding: '2rem', backgroundColor: '#ecf0f1', borderRadius: '8px' }}>
                <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Purchase Orders</h3>
                <p style={{ marginBottom: '1.5rem', color: '#7f8c8d' }}>
                  Create and manage purchase orders with payment terms.
                </p>
                <Link to="/purchase-orders" className="btn btn-primary">
                  View Purchase Orders
                </Link>
              </div>

              <div style={{ padding: '2rem', backgroundColor: '#ecf0f1', borderRadius: '8px' }}>
                <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Invoices</h3>
                <p style={{ marginBottom: '1.5rem', color: '#7f8c8d' }}>
                  Create and manage invoices with detailed line items and tracking.
                </p>
                <Link to="/invoices" className="btn btn-primary">
                  View Invoices
                </Link>
              </div>

              <div style={{ padding: '2rem', backgroundColor: '#ecf0f1', borderRadius: '8px' }}>
                <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Projects</h3>
                <p style={{ marginBottom: '1.5rem', color: '#7f8c8d' }}>
                  Organize work into projects with billing rates for time tracking.
                </p>
                <Link to="/projects" className="btn btn-primary">
                  View Projects
                </Link>
              </div>

              <div style={{ padding: '2rem', backgroundColor: '#ecf0f1', borderRadius: '8px' }}>
                <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Billing Rates</h3>
                <p style={{ marginBottom: '1.5rem', color: '#7f8c8d' }}>
                  Set up billing rates for different types of work on projects.
                </p>
                <Link to="/billing-rates" className="btn btn-primary">
                  View Billing Rates
                </Link>
              </div>

              <div style={{ padding: '2rem', backgroundColor: '#ecf0f1', borderRadius: '8px' }}>
                <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>User Assignments</h3>
                <p style={{ marginBottom: '1.5rem', color: '#7f8c8d' }}>
                  Assign users to billing rates for time tracking authorization.
                </p>
                <Link to="/user-rates" className="btn btn-primary">
                  Manage Assignments
                </Link>
              </div>
            </div>

            <div className="card" style={{ marginTop: '2rem' }}>
              <h3>Quick Actions</h3>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                <Link to="/customers/new" className="btn btn-success">
                  Add New Company
                </Link>
                <Link to="/purchase-orders/new" className="btn btn-success">
                  Create Purchase Order
                </Link>
                <Link to="/invoices/new" className="btn btn-success">
                  Create Invoice
                </Link>
                <Link to="/projects/new" className="btn btn-success">
                  Create Project
                </Link>
                <Link to="/billing-rates/new" className="btn btn-success">
                  Create Billing Rate
                </Link>
              </div>
            </div>

            <div className="card" style={{ marginTop: '2rem' }}>
              <h3>Time Tracking</h3>
              <p style={{ color: '#7f8c8d', marginBottom: '1rem' }}>
                Enter your time and track hours worked on projects. Review and submit
                your monthly timesheet when ready.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <Link to="/time-entry" className="btn btn-primary">
                  Enter Time
                </Link>
                <Link to="/my-timesheets" className="btn btn-secondary">
                  Review My Timesheets
                </Link>
              </div>
            </div>

            {myRates.length > 0 && (
              <div className="card" style={{ marginTop: '2rem' }}>
                <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Your Assigned Billing Rates</h3>
                <p style={{ color: '#7f8c8d', marginBottom: '1rem' }}>
                  You are authorized to bill time to the following rates:
                </p>
                <ul style={{ 
                  listStyle: 'none', 
                  padding: 0, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '0.75rem' 
                }}>
                  {myRates.map((rate) => (
                    <li
                      key={rate.id}
                      style={{
                        padding: '1rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        border: '1px solid #e1e8ed',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <strong style={{ color: '#2c3e50' }}>{rate.name}</strong>
                        {rate.description && (
                          <p style={{ color: '#7f8c8d', fontSize: '0.9rem', margin: '0.25rem 0 0 0' }}>
                            {rate.description}
                          </p>
                        )}
                      </div>
                      <span style={{ 
                        color: '#3498db', 
                        fontSize: '0.85rem', 
                        fontWeight: 'bold',
                        backgroundColor: '#ecf6fc',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                      }}>
                        {rate.project_code}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {!isAdmin && (
          <div style={{ padding: '2rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Your Billing Rates</h3>
            {loading ? (
              <p style={{ color: '#95a5a6' }}>Loading your billing rates...</p>
            ) : myRates.length > 0 ? (
              <div>
                <p style={{ color: '#7f8c8d', marginBottom: '1rem' }}>
                  You are authorized to bill time to the following rates:
                </p>
                <ul style={{ 
                  listStyle: 'none', 
                  padding: 0, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '0.75rem' 
                }}>
                  {myRates.map((rate) => (
                    <li
                      key={rate.id}
                      style={{
                        padding: '1rem',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        border: '1px solid #e1e8ed',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <strong style={{ color: '#2c3e50' }}>{rate.name}</strong>
                        {rate.description && (
                          <p style={{ color: '#7f8c8d', fontSize: '0.9rem', margin: '0.25rem 0 0 0' }}>
                            {rate.description}
                          </p>
                        )}
                      </div>
                      <span style={{ 
                        color: '#3498db', 
                        fontSize: '0.85rem', 
                        fontWeight: 'bold',
                        backgroundColor: '#ecf6fc',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                      }}>
                        {rate.project_code}
                      </span>
                    </li>
                  ))}
                </ul>
                <p style={{ color: '#95a5a6', marginTop: '1.5rem', fontSize: '0.9rem' }}>
                  <strong>Coming Soon:</strong> Time tracking features to log hours against these rates.
                </p>
              </div>
            ) : (
              <div>
                <p style={{ color: '#95a5a6' }}>
                  No billing rates have been assigned to you yet.
                </p>
                <p style={{ color: '#95a5a6', marginTop: '1rem' }}>
                  Contact your administrator to get assigned to projects.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {isSuperAdmin && (
        <div className="card">
          <h3 style={{ color: '#e74c3c' }}>Super Admin Tools</h3>
          <div style={{ marginTop: '1rem' }}>
            <a href="/admin" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              Open Django Admin
            </a>
            <p style={{ marginTop: '1rem', color: '#7f8c8d', fontSize: '0.9rem' }}>
              Manage users, groups, and system settings
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
